/* eslint-disable @typescript-eslint/dot-notation */
import { Component, ViewEncapsulation } from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { FormControl } from '@angular/forms';
import { DateInputEnum } from '../../models/date-input.enum';
import { DateFormatEnum } from '../../models/date-format.enum';
import { DetectUserAgent } from '../../utils/detect-user-agent';

/**
 * Editor Custom AgGrid for Date DateTime Time Input.
 * Params:
 * - inputType: (Optional) it allows to specify 'DATE_TIME', 'DATE' or 'TIME'. Default value DATE.
 * - inputFormat: (Optional) it allows to specify 'SHORT' 'LONG' format for DATE_TIME, DATE e TIME. Default value SHORT.
 * - required: (Optional) It allows to specify required validator
 * - inlineEditor (Default true) it allows to specify the preference between online or popup editor version.
 *  - dateInputFormat:
 *  - timeInputFormat:
 *  - dateTimeInputFormat:
 *  - dateSeparator:
 *  - timeSeparator:
 *  NOTE: when in the AgGrid is enabled the fullRow editing this input param is ignored and the editor will be inline
 */
@Component({
    selector: 'se-masked-date-cell-editor',
    templateUrl: './masked-date-cell-editor.component.html',
    styleUrls: ['./masked-date-cell-editor.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class MaskedDateCellEditorComponent implements ICellEditorAngularComp {
    public inputType: DateInputEnum;
    public inputFormat: DateFormatEnum;
    public editInline: boolean;
    public required: boolean;
    public dateInputFormat: string;
    public timeInputFormat: string;
    public dateTimeInputFormat: string;
    public dateSeparator: string;
    public timeSeparator: string;

    value: string;
    originalValue: Date;
    params: ICellEditorParams;
    cellWidth: string;
    cellStartEdited: boolean;
    formControl: FormControl;
    inlineEditor: boolean;

    constructor(private detectUserAgent: DetectUserAgent) {
    }

    agInit(params: ICellEditorParams): void {
        this.params = params;
        this.cellWidth = params.column.getActualWidth() + 'px';
        this.inlineEditor = (this.params.api.getEditingCells().length) > 1;

        // Recupero parametri input
        this.configCellEditor();
        this.prepareValueForEditor(params.value);
        this.cellStartEdited = this.params.cellStartedEdit;
    }


    getValue(): any {
        // Alla chiusura dell'editor se il campo è valido aggiorniamo il valore altrimenti no

        // Nel caso in cui il valore non sia stato modificato mandiamo indietro il value originale così da non scatenare l'evento di
        // ModelChange (bordo giallo al pannello)
        if (this.formControl && this.formControl.value === this.value) {
            return this.params.value;
        }

        // NOTA Sonia: per capire se il campo è valido dobbiamo verificare che ci sia una data valida
        if (this.verifyControlValueValidity()) {
            this.value = this.formControl.value;
            this.updateOriginalValue();
            return this.preserveDateOutput(this.originalValue)
        }
        return null;
    }

    convertInputToUTC(d: Date): Date {
        const year = d.getUTCFullYear(),
         month = d.getUTCMonth(),
         day = d.getUTCDate(),
         hour = d.getUTCHours(),
         min = d.getUTCMinutes(),
         sec = d.getUTCSeconds(),
         ms = d.getUTCMilliseconds();
         return new Date(year, month, day, hour, min, sec, ms);
    }

    preserveDateOutput(d: Date): string {
        const year = d.getFullYear(),
        month = d.getMonth(),
        day = d.getDate(),
        hour = d.getHours(),
        min = d.getMinutes(),
        sec = d.getSeconds(),
        ms = d.getMilliseconds()
        return new Date(Date.UTC(year, month, day, hour, min, sec, ms)).toISOString()

    }

    verifyControlValueValidity(): boolean {
        let valueToCheck = this.formControl.value;
        // check date
        if (!this.checkArray(valueToCheck.substring(0, 10).split('-'))) {
            return false;
        }
        // checktime
        if (!this.checkArray(valueToCheck.substring(11).split(':'))) {
            return false;
        }
        if (this.formControl.value.length < 9) { // se è solo ora
            valueToCheck = new Date();
            valueToCheck.setHours(...this.formControl.value.split(':'));
        }
        return !!Date.parse(valueToCheck);
    }

    checkArray(array: string[]): boolean {
        if (!array || array.length === 0) {
            return false;
        }
        if (array.length === 1) {
            return true;
        }
        for (let index = 0; index < array.length; index++) {
            if (isNaN(Number(array[index])) && array[index] !== '00') {
                return false;
            }
        }

        return true;
    }

    isPopup(): boolean {
        return !this.editInline;
    }

    _onFormReady(editorFromControl: FormControl) {
        this.formControl = editorFromControl;
    }

    private configCellEditor() {
        const inputTypeParam = this.params['inputType'];
        this.inputType = inputTypeParam ? inputTypeParam : DateInputEnum.DATE;
        // NOTA Sonia questa parte in questo componente non serve visto che non viene impostato mai type diverso da text
        const isIEOrFireFoxOrSafari = this.detectUserAgent.isIE() || this.detectUserAgent.isFF()
            || this.detectUserAgent.isSafari();

        if (this.inputType === DateInputEnum.DATE_TIME && isIEOrFireFoxOrSafari) {
            this.inputType = DateInputEnum.DATE_TIME_OLD;
        }

        const inputFormatParam = this.params['inputFormat'];
        this.inputFormat = inputFormatParam ? inputFormatParam : DateFormatEnum.SHORT;

        this.editInline = this.params['inlineEditor'];
        this.editInline = (this.editInline !== null && this.editInline !== undefined) ? this.editInline : true;

        this.inlineEditor = (this.params.api.getEditingCells().length) === 1 ? this.editInline : true;
        this.required = this.params['required'];

        this.dateInputFormat = this.params['dateInputFormat'] || null;
        this.timeInputFormat = this.params['timeInputFormat'] || null;
        this.dateTimeInputFormat = this.params['dateTimeInputFormat'] || null;
        this.dateSeparator = this.params['dateSeparator'] || null;
        this.timeSeparator = this.params['timeSeparator'] || null;
    }

    private prepareValueForEditor(value: string) {if (value === null || value === "" || !value) {
            this.originalValue = null;
            this.value = '';
            return;
        }


        switch (this.inputType) {
            case DateInputEnum.DATE:
                this.formatDate(value);
                break;
            case DateInputEnum.DATE_TIME:
                this.formatDateTime(value);
                break;
            case DateInputEnum.TIME:
                this.formatTime(value);
                break;
            default:
                console.error('parametro inputType non corretto: ', this.inputType);
                this.formatDate(value);
        }
    }

    private isValidDate(d) {
        return d instanceof Date && !isNaN(d.getTime());
    }

    private formatDate(value: string) {
        this.originalValue = new Date(value);
        switch (this.inputFormat) {
            case DateFormatEnum.SHORT:
            case DateFormatEnum.LONG: // TODO capire che cosa è un formato lungo in input di una data
                if (this.isValidDate(this.originalValue)) {
                    this.value = this.convertDateToString();
                }
                break;
            default:
                console.error('parametro inputFormat non corretto: ', this.inputFormat);
                if (new Date(value) instanceof Date) {
                    this.convertDateToString();
                }
        }
    }

    private formatDateTime(value: string) {

        // 'SHORT': equivalent to 'yyyy-MM-ddThh:mm'
        // 'LONG': equivalent to 'yyyy-MM-ddThh:mm:ss'
        this.originalValue = new Date(value);
        this.value = '';

        if (this.isValidDate(this.originalValue)) {
            this.value = this.convertDateToString();
            this.value += 'T' + this.convertTimeToString();
        }
    }

    private formatTime(value: string) {
        // 'SHORT': equivalent to 'hh:mm'
        // 'LONG': equivalent to 'hh:mm:ss'
        this.originalValue = this.convertInputToUTC (new Date(value));
        if (this.isValidDate(this.originalValue)) {
            this.value = this.convertTimeToString();
        } else {
            this.value = value;
        }
    }

    private prependZero(n: number): string {
        if (n < 10) {
            return '0' + n;
        }
        return '' + n;
    }

    private convertDateToString(): string {
        const year = this.originalValue.getFullYear();
        const month = this.prependZero(this.originalValue.getMonth() + 1);
        const day = this.prependZero(this.originalValue.getDate());
        const formattedDate = year + '-' + month + '-' + day;
        return formattedDate;
    }

    private convertTimeToString(): string {
       
        const hours = this.prependZero(this.originalValue.getHours());
        const minutes = this.prependZero(this.originalValue.getMinutes());
        const seconds = this.prependZero(this.originalValue.getSeconds());
        let formattedString = '';
        formattedString += hours + ':' + minutes;

        if (this.inputFormat === DateFormatEnum.LONG) {
            formattedString += ':' + seconds;
        }
        return formattedString;
    }

    private updateOriginalValue() {
        if (this.value === '') {
            this.originalValue = null;
            return;
        }

        switch (this.inputType) {
            case DateInputEnum.DATE_TIME:
                this.originalValue = new Date(this.value);
                break;
            case DateInputEnum.DATE:
                // prendiamo la data aggioranta da this.value ma il time lasciamo quello originale
                const generatedDate = new Date(this.value);
                if (this.originalValue) {
                 generatedDate.setHours(this.originalValue.getUTCHours(), this.originalValue.getUTCMinutes(), this.originalValue.getUTCSeconds());
                }
                this.originalValue = generatedDate;
                break;
            case DateInputEnum.TIME:
                // prendiamo il time da this.value ma la data lasciamo quella originale
                if (!this.originalValue) {
                    this.originalValue = new Date();
                }
                const hours = this.value.split(':');
                if (hours.length === 3) {
                    this.originalValue.setHours(parseInt(hours[0], 10), parseInt(hours[1], 10), parseInt(hours[2], 10));
                } else if (hours.length === 2) {
                    this.originalValue.setHours(parseInt(hours[0], 10), parseInt(hours[1], 10));
                } else {
                    this.originalValue.setHours(0);
                }
                break;
        }
    }
}
